import React from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useAuth } from '../../context/AuthContext';
import { 
  LayoutDashboard, 
  Beef, 
  Users, 
  Wallet, 
  MessageSquare, 
  Settings, 
  LogOut,
  ShieldCheck,
  Shield,
  Menu,
  X,
  Key,
  Trash2,
  Truck
} from 'lucide-react';
import AdminProfileModal from '../shared/AdminProfileModal';

export default function DashboardLayout() {
  const { userProfile, isSuperAdmin, signOut } = useAuth();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false);
  const [isProfileModalOpen, setIsProfileModalOpen] = React.useState(false);
  const [unreadCount, setUnreadCount] = React.useState(0);

  // Unread Ticket Dinleyici
  React.useEffect(() => {
    if (!userProfile) return;
    
    let q;
    if (userProfile.role === 'superAdmin') {
      q = query(collection(db, 'tickets'));
    } else {
      if (!userProfile.assignedBranches || userProfile.assignedBranches.length === 0) return;
      q = query(collection(db, 'tickets'), where('branchId', 'in', userProfile.assignedBranches));
    }

    const unsubscribe = onSnapshot(q, (snapshot) => {
      let count = 0;
      snapshot.forEach(doc => {
        const ticket = doc.data();
        if (ticket.status === 'open' && ticket.messages?.length > 0) {
          const lastMsg = ticket.messages[ticket.messages.length - 1];
          if (lastMsg.senderRole === 'customer') count++;
        }
      });
      setUnreadCount(count);
    });

    return () => unsubscribe();
  }, [userProfile]);

  // Navigasyon Menüsü
  const navigation = [
    { name: 'Dashboard Özeti', to: '/dashboard', icon: LayoutDashboard },
    { name: 'Hayvan Yönetimi', to: '/dashboard/kurbanlar', icon: Beef },
    { name: 'Hissedarlar', to: '/dashboard/hissedarlar', icon: Users },
    { name: 'Tahsilat', to: '/dashboard/tahsilat', icon: Wallet },
    { name: 'Müşteri Mesajları', to: '/dashboard/mesajlar', icon: MessageSquare },
  ];

  // Ekip / Alt Şube Yönetimi tüm adminlere açık, subAdmin'lere KAPALI
  if (userProfile?.role === 'admin' || isSuperAdmin) {
    navigation.push({ name: isSuperAdmin ? 'Yetkili Yönetimi' : 'Alt Şube ve Ekip', to: '/dashboard/adminler', icon: ShieldCheck });
  }
  
  if (isSuperAdmin) {
    navigation.push({ name: 'Sistem Ayarları', to: '/dashboard/ayarlar', icon: Settings });
    navigation.push({ name: 'Çöp Kutusu', to: '/dashboard/cop-kutusu', icon: Trash2 });
    navigation.push({ name: 'Eve Teslimat', to: '/dashboard/teslimat', icon: Truck });
  }

  const closeMobileMenu = () => setIsMobileMenuOpen(false);

  const NavContent = () => (
    <div className="flex flex-col h-full">
      {/* Brand */}
      <div className="h-16 flex items-center gap-3 px-6 shrink-0 border-b border-navy-700/50">
        <div className="w-8 h-8 rounded-lg overflow-hidden flex items-center justify-center shadow-md bg-transparent">
          <img src="/angry-bull-logo.png" alt="Haşimoğlu Logo" className="w-full h-full object-cover" />
        </div>
        <div className="flex flex-col">
          <span className="text-sm font-bold text-white leading-tight tracking-tight">Haşimoğlu Çiftliği</span>
          <span className="text-[10px] text-navy-400 font-medium">BETA 1.1</span>
        </div>
      </div>

      {/* User Info */}
      <div className="px-6 py-5 border-b border-navy-700/50">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-full bg-navy-800 flex items-center justify-center border border-navy-600/50">
            {isSuperAdmin ? <ShieldCheck className="w-5 h-5 text-gold-400" /> : <Shield className="w-5 h-5 text-forest-400" />}
          </div>
          <div className="flex flex-col overflow-hidden">
            <span className="text-sm font-medium text-white truncate">
              {userProfile?.displayName || userProfile?.email}
            </span>
            <span className="text-xs text-navy-300">
              {isSuperAdmin ? 'Süper Admin' : 'Admin'}
            </span>
          </div>
        </div>
        {/* Atanmış Şubeler (Süper Admin değilse göster) */}
        {!isSuperAdmin && userProfile?.assignedBranches && (
           <div className="flex flex-wrap gap-1 mt-2">
             {userProfile.assignedBranches.map((b) => (
                <span key={b} className="text-[10px] px-2 py-0.5 rounded-full bg-navy-800 text-navy-300 border border-navy-700">
                  {b.toUpperCase()}
                </span>
             ))}
           </div>
        )}
      </div>

      {/* Nav Links */}
      <nav className="flex-1 px-4 py-6 space-y-1 overflow-y-auto">
        <p className="px-2 text-xs font-semibold text-navy-500 uppercase tracking-wider mb-2">
          Ana Menü
        </p>
        {navigation.map((item) => {
          const isActive = location.pathname === item.to;
          return (
            <NavLink
              key={item.name}
              to={item.to}
              onClick={closeMobileMenu}
              className={`flex items-center justify-between px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 group ${
                isActive 
                  ? 'bg-gradient-to-r from-forest-600/20 to-transparent text-forest-400 border-l-2 border-forest-500' 
                  : 'text-navy-300 hover:bg-navy-800/50 hover:text-white border-l-2 border-transparent'
              }`}
            >
              <div className="flex items-center gap-3">
                 <item.icon className={`w-5 h-5 transition-colors ${isActive ? 'text-forest-400' : 'text-navy-400 group-hover:text-white'}`} />
                 {item.name}
              </div>
              {item.to === '/dashboard/mesajlar' && unreadCount > 0 && (
                <span className="bg-red-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full animate-pulse shadow-sm shadow-red-500/50">
                  {unreadCount}
                </span>
              )}
            </NavLink>
          );
        })}
      </nav>

      {/* Footer / Logout */}
      <div className="p-4 border-t border-navy-700/50 space-y-2">
        <button
          onClick={() => setIsProfileModalOpen(true)}
          className="flex items-center gap-3 px-3 py-2 w-full rounded-xl text-sm font-medium text-navy-300 hover:bg-navy-800 hover:text-white transition-colors"
        >
          <Key className="w-5 h-5 opacity-80" />
          Şifremi Değiştir
        </button>
        <button
          onClick={signOut}
          className="flex items-center gap-3 px-3 py-2 w-full rounded-xl text-sm font-medium text-danger-400 hover:bg-danger-500/10 transition-colors"
        >
          <LogOut className="w-5 h-5 opacity-80" />
          Güvenli Çıkış
        </button>
      </div>
    </div>
  );

  return (
    <div className="flex h-screen bg-navy-950 overflow-hidden">
      
      {/* Desktop Sidebar */}
      <aside className="hidden md:flex w-64 flex-col bg-navy-900/40 border-r border-navy-700/50 backdrop-blur-xl">
        <NavContent />
      </aside>

      {/* Mobile Drawer */}
      <div className={`md:hidden fixed inset-0 z-50 transform transition-transform duration-300 ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={closeMobileMenu} />
        <aside className="absolute inset-y-0 left-0 w-64 bg-navy-900 border-r border-navy-700/50 shadow-2xl">
          <button 
            onClick={closeMobileMenu}
            className="absolute top-4 right-4 text-navy-400 hover:text-white"
          >
            <X className="w-6 h-6" />
          </button>
          <NavContent />
        </aside>
      </div>

      {/* Content Area */}
      <div className="flex-1 flex flex-col h-screen overflow-hidden">
        {/* Mobile Header */}
        <header className="md:hidden h-16 flex items-center justify-between px-4 glass border-b border-navy-700/30 shrink-0">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg overflow-hidden flex items-center justify-center bg-transparent">
              <img src="/angry-bull-logo.png" alt="Haşimoğlu Logo" className="w-full h-full object-cover" />
            </div>
            <span className="text-sm font-bold text-white">Haşimoğlu Çiftliği</span>
          </div>
          <button onClick={() => setIsMobileMenuOpen(true)} className="p-2 text-navy-300 hover:text-white">
            <Menu className="w-6 h-6" />
          </button>
        </header>

        {/* Main Content (Outlet) */}
        <main className="flex-1 overflow-y-auto p-4 md:p-6 lg:p-8 custom-scrollbar">
          <Outlet />
        </main>
      </div>

      <AdminProfileModal 
        isOpen={isProfileModalOpen} 
        onClose={() => setIsProfileModalOpen(false)} 
      />
    </div>
  );
}
